import {
  ApiError,
  queryKeys,
  type RentalNotificationSettings,
  type UpdateRentalNotificationSettingsInput,
  unknownRentalPlaceholders,
} from '@mevaser/shared'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

/** "3, 2" → [3, 2]; null when any entry isn't a whole number of days from 1 to 90. */
function parseLeadDays(text: string): number[] | null {
  const parts = text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  const days = parts.map(Number)
  if (days.some((d) => !Number.isInteger(d) || d < 1 || d > 90)) return null
  return [...new Set(days)].sort((a, b) => b - a)
}

/** What's wrong with a piece of reminder wording, or undefined when it's fine. */
function wordingProblem(text: string, max: number): string | undefined {
  const trimmed = text.trim()
  if (trimmed === '') return 'The wording can’t be empty'
  if (trimmed.length > max) return `Keep it under ${max} characters`
  const unknown = unknownRentalPlaceholders(trimmed)
  if (unknown.length > 0) return `Unknown placeholder: {{${unknown[0]}}}`
  return undefined
}

const PLACEHOLDER_HINT =
  'Placeholders {{customerName}}, {{rentalNumber}}, {{dueDate}} and {{storeName}} are filled in for each rental. Text messages always end with "Reply STOP to opt out."'

function Wording(props: {
  label: string
  value: string
  onChange: (value: string) => void
  max: number
  rows?: number
  disabled: boolean
  /** One line (a subject) rather than a text box. */
  single?: boolean
}) {
  const problem = wordingProblem(props.value, props.max)
  return (
    <Field label={props.label} error={problem}>
      {(fieldProps) =>
        props.single ? (
          <Input
            {...fieldProps}
            value={props.value}
            disabled={props.disabled}
            invalid={problem !== undefined}
            onChange={(e) => props.onChange(e.target.value)}
          />
        ) : (
          <Textarea
            {...fieldProps}
            rows={props.rows ?? 4}
            value={props.value}
            disabled={props.disabled}
            onChange={(e) => props.onChange(e.target.value)}
          />
        )
      }
    </Field>
  )
}

/**
 * Automated rental reminders and overdue alerts (t1082): the reminder email to
 * the customer before a rental is due, and what happens when one goes overdue.
 * Everything starts off. The notices go out once a day, mid-morning shop time.
 * The customer-facing wording, and text-message versions of each reminder, are
 * editable here (t1107).
 */
export function RentalSettingsTab() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.rentalNotificationSettings(),
    queryFn: () => api.get<RentalNotificationSettings>('/settings/rentals'),
  })

  const [remindersEnabled, setRemindersEnabled] = useState(false)
  const [leadDaysText, setLeadDaysText] = useState('3')
  const [reminderSubject, setReminderSubject] = useState('')
  const [reminderBody, setReminderBody] = useState('')
  const [reminderSmsEnabled, setReminderSmsEnabled] = useState(false)
  const [reminderSmsBody, setReminderSmsBody] = useState('')
  const [staffAlertEnabled, setStaffAlertEnabled] = useState(false)
  const [alertEmail, setAlertEmail] = useState('')
  const [customerEmailEnabled, setCustomerEmailEnabled] = useState(false)
  const [overdueSubject, setOverdueSubject] = useState('')
  const [overdueBody, setOverdueBody] = useState('')
  const [overdueSmsEnabled, setOverdueSmsEnabled] = useState(false)
  const [overdueSmsBody, setOverdueSmsBody] = useState('')
  const [callEnabled, setCallEnabled] = useState(false)
  const [callScript, setCallScript] = useState('')

  useEffect(() => {
    if (!data) return
    setRemindersEnabled(data.remindersEnabled)
    setLeadDaysText(data.reminderLeadDays.join(', '))
    setReminderSubject(data.reminderEmailSubject)
    setReminderBody(data.reminderEmailBody)
    setReminderSmsEnabled(data.reminderSmsEnabled)
    setReminderSmsBody(data.reminderSmsBody)
    setStaffAlertEnabled(data.overdueStaffAlertEnabled)
    setAlertEmail(data.overdueAlertEmail ?? '')
    setCustomerEmailEnabled(data.overdueCustomerEmailEnabled)
    setOverdueSubject(data.overdueCustomerEmailSubject)
    setOverdueBody(data.overdueCustomerEmailBody)
    setOverdueSmsEnabled(data.overdueCustomerSmsEnabled)
    setOverdueSmsBody(data.overdueCustomerSmsBody)
    setCallEnabled(data.overdueCallEnabled)
    setCallScript(data.overdueCallScript)
  }, [data])

  const save = useMutation({
    mutationFn: (patch: UpdateRentalNotificationSettingsInput) =>
      api.patch('/settings/rentals', patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentalNotificationSettings() })
      toast.success('Saved')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const leadDays = parseLeadDays(leadDaysText)
  const leadDaysInvalid = leadDays === null || leadDays.length > 5 || leadDays.length === 0
  const leadDaysProblem = (remindersEnabled || reminderSmsEnabled) && leadDaysInvalid
  const emailMissing = staffAlertEnabled && alertEmail.trim() === ''
  const wordingInvalid = [
    wordingProblem(reminderSubject, 200),
    wordingProblem(reminderBody, 2000),
    wordingProblem(reminderSmsBody, 500),
    wordingProblem(overdueSubject, 200),
    wordingProblem(overdueBody, 2000),
    wordingProblem(overdueSmsBody, 500),
    wordingProblem(callScript, 1000),
  ].some((problem) => problem !== undefined)

  const onSave = () =>
    save.mutate({
      remindersEnabled,
      ...(leadDays && !leadDaysInvalid ? { reminderLeadDays: leadDays } : {}),
      reminderEmailSubject: reminderSubject.trim(),
      reminderEmailBody: reminderBody.trim(),
      reminderSmsEnabled,
      reminderSmsBody: reminderSmsBody.trim(),
      overdueStaffAlertEnabled: staffAlertEnabled,
      overdueAlertEmail: alertEmail.trim() === '' ? null : alertEmail.trim(),
      overdueCustomerEmailEnabled: customerEmailEnabled,
      overdueCustomerEmailSubject: overdueSubject.trim(),
      overdueCustomerEmailBody: overdueBody.trim(),
      overdueCustomerSmsEnabled: overdueSmsEnabled,
      overdueCustomerSmsBody: overdueSmsBody.trim(),
      overdueCallEnabled: callEnabled,
      overdueCallScript: callScript.trim(),
    })

  return (
    <div className="space-y-5">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Due-date reminders</CardTitle>
          <CardDescription>
            Email or text the customer a few days before their rental is due back. Returned and
            cancelled rentals, open-ended rentals and customers with no email or mobile number on
            file are skipped. Notices go out once a day, mid-morning shop time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Email due-date reminders</span>
            <Switch
              checked={remindersEnabled}
              disabled={isLoading}
              onCheckedChange={setRemindersEnabled}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between rounded-md border border-border p-2.5">
              <span className="text-sm">Text due-date reminders</span>
              <Switch
                checked={reminderSmsEnabled}
                disabled={isLoading}
                onCheckedChange={setReminderSmsEnabled}
              />
            </div>
            <p className="text-xs text-foreground-muted">
              Texts need Twilio set up under Integrations, and go to customers with a mobile number
              on file who haven’t had texting turned off.
            </p>
          </div>
          <Field
            label="Days before the due date"
            hint="Separate several with commas — “3, 2” sends one reminder three days out and another two days out. Applies to both emails and texts."
            error={leadDaysProblem ? 'Up to 5 whole numbers of days, from 1 to 90' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                value={leadDaysText}
                disabled={isLoading}
                invalid={leadDaysProblem}
                onChange={(e) => setLeadDaysText(e.target.value)}
              />
            )}
          </Field>

          <p className="text-xs text-foreground-muted">{PLACEHOLDER_HINT}</p>
          <Wording
            label="Reminder email subject"
            single
            max={200}
            value={reminderSubject}
            disabled={isLoading}
            onChange={setReminderSubject}
          />
          <Wording
            label="Reminder email wording"
            max={2000}
            rows={8}
            value={reminderBody}
            disabled={isLoading}
            onChange={setReminderBody}
          />
          <Wording
            label="Reminder text message wording"
            max={500}
            rows={3}
            value={reminderSmsBody}
            disabled={isLoading}
            onChange={setReminderSmsBody}
          />
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>When a rental goes overdue</CardTitle>
          <CardDescription>
            A rental is marked overdue overnight once its due date has passed. These go out the next
            morning, once per rental. Extending the due date starts the cycle again for the new
            date.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Email an alert to the shop</span>
            <Switch
              checked={staffAlertEnabled}
              disabled={isLoading}
              onCheckedChange={setStaffAlertEnabled}
            />
          </div>
          {staffAlertEnabled ? (
            <Field
              label="Send the alert to"
              error={emailMissing ? 'Add an email address for the alert' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={alertEmail}
                  invalid={emailMissing}
                  onChange={(e) => setAlertEmail(e.target.value)}
                />
              )}
            </Field>
          ) : null}

          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Email the customer a follow-up</span>
            <Switch
              checked={customerEmailEnabled}
              disabled={isLoading}
              onCheckedChange={setCustomerEmailEnabled}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Text the customer a follow-up</span>
            <Switch
              checked={overdueSmsEnabled}
              disabled={isLoading}
              onCheckedChange={setOverdueSmsEnabled}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between rounded-md border border-border p-2.5">
              <span className="text-sm">Call the customer with an automated message</span>
              <Switch checked={callEnabled} disabled={isLoading} onCheckedChange={setCallEnabled} />
            </div>
            <p className="text-xs text-foreground-muted">
              Calls the customer's phone number once, at the same mid-morning time, and reads out
              the script below. Needs Twilio set up under Integrations; no call is made if it isn't,
              or if the customer has no phone number.
            </p>
          </div>

          <p className="text-xs text-foreground-muted">{PLACEHOLDER_HINT}</p>
          <Wording
            label="Overdue email subject"
            single
            max={200}
            value={overdueSubject}
            disabled={isLoading}
            onChange={setOverdueSubject}
          />
          <Wording
            label="Overdue email wording"
            max={2000}
            rows={8}
            value={overdueBody}
            disabled={isLoading}
            onChange={setOverdueBody}
          />
          <Wording
            label="Overdue text message wording"
            max={500}
            rows={3}
            value={overdueSmsBody}
            disabled={isLoading}
            onChange={setOverdueSmsBody}
          />
          <Wording
            label="Phone call script"
            max={1000}
            rows={5}
            value={callScript}
            disabled={isLoading}
            onChange={setCallScript}
          />
        </CardContent>
      </Card>

      <Button
        variant="primary"
        onClick={onSave}
        disabled={save.isPending || isLoading || leadDaysProblem || emailMissing || wordingInvalid}
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </Button>
    </div>
  )
}
